/**
 * ApiClient — REST client with shared refresh mutex (401 only), TokenStorage, no cookies.
 */
import { tokenStorage } from "@/auth/tokenStorage";
import type { AuthSessionResponse } from "@/auth/authTypes";

export const AUTH_LOGOUT_EVENT = "dreamboy-auth-logout-required";

/** Thrown when refresh failed after 401; {@link isAuthSessionExpiredError} in UI to skip generic error toasts. */
export type AuthSessionExpiredError = Error & { authSessionExpired: true; status: number };

export function createAuthSessionExpiredError(status: number): AuthSessionExpiredError {
  const e = new Error("AUTH_SESSION_EXPIRED") as AuthSessionExpiredError;
  e.authSessionExpired = true;
  e.status = status;
  return e;
}

export function isAuthSessionExpiredError(err: unknown): err is AuthSessionExpiredError {
  return typeof err === "object" && err !== null && (err as AuthSessionExpiredError).authSessionExpired === true;
}

function dispatchAuthLogout(): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(AUTH_LOGOUT_EVENT));
  }
}

export class ApiClient {
  baseURL: string;
  token: string | null = null;
  fakeDelay: number;
  defaultHeaders: Record<string, string>;

  private refreshMutex: Promise<boolean> | null = null;

  constructor(options: {
    baseURL?: string;
    token?: string | null;
    fakeDelay?: number;
    defaultHeaders?: Record<string, string>;
  } = {}) {
    this.baseURL = options.baseURL ?? import.meta.env.VITE_API_BASE_URL ?? "";
    this.fakeDelay = options.fakeDelay ?? 0;
    this.defaultHeaders = {
      "Content-Type": "application/json",
      ...options.defaultHeaders,
    };
    if (options.token != null) this.token = options.token;
  }

  /** Load access token from TokenStorage into memory (call on app start). */
  async hydrateFromStorage(): Promise<void> {
    this.token = await tokenStorage.getAccessToken();
  }

  async applyAuthSession(session: AuthSessionResponse): Promise<void> {
    await tokenStorage.setSession(session);
    this.token = session.accessToken;
  }

  buildURL(endpoint: string): string {
    const clean = endpoint.startsWith("/") ? endpoint.slice(1) : endpoint;
    return `${this.baseURL}/${clean}`;
  }

  getAuthHeader(): Record<string, string> {
    return this.token ? { Authorization: `Bearer ${this.token}` } : {};
  }

  getHeaders(): Record<string, string> {
    return { ...this.defaultHeaders, ...this.getAuthHeader() };
  }

  async simulateDelay(delay = this.fakeDelay): Promise<void> {
    if (delay > 0) await new Promise((r) => setTimeout(r, delay));
  }

  /**
   * Runs refresh exactly once for concurrent 401s; returns true if new access token is available.
   */
  private async runSharedRefresh(): Promise<boolean> {
    if (this.refreshMutex) return this.refreshMutex;

    this.refreshMutex = (async () => {
      try {
        const refreshToken = await tokenStorage.getRefreshToken();
        if (!refreshToken) return await this.signInWithDeviceLogin();

        const res = await fetch(`${this.baseURL}/auth/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refreshToken }),
        });

        if (!res.ok) return await this.signInWithDeviceLogin();

        const data = (await res.json()) as AuthSessionResponse;
        if (!data?.accessToken) return false;

        await this.applyAuthSession(data);
        return true;
      } catch {
        return false;
      } finally {
        this.refreshMutex = null;
      }
    })();

    return this.refreshMutex;
  }

  /**
   * Last resort when the refresh token is gone or refused: an account
   * without e-mail signs back in with its device secret. A refused secret
   * is final (account got credentials, was deleted, or the secret is
   * wrong), so the stored device login is dropped with the tokens. Network
   * failures keep it for the next attempt.
   */
  async signInWithDeviceLogin(): Promise<boolean> {
    const login = await tokenStorage.getDeviceLogin();
    if (!login) return false;
    try {
      const res = await fetch(`${this.baseURL}/auth/login-by-id`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(login),
      });
      if (res.status === 401) {
        await tokenStorage.clear();
        await tokenStorage.clearDeviceLogin();
        return false;
      }
      if (!res.ok) return false;
      const data = (await res.json()) as AuthSessionResponse;
      if (!data?.accessToken) return false;
      await this.applyAuthSession(data);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * For SSE: return access token. If forceRefresh, clear memory and run refresh (e.g. after 401 on stream).
   */
  async ensureValidToken(opts?: { forceRefresh?: boolean }): Promise<string | null> {
    if (opts?.forceRefresh) {
      this.token = null;
      const ok = await this.runSharedRefresh();
      if (!ok) dispatchAuthLogout();
      return ok ? this.token : null;
    }
    if (this.token) return this.token;
    await this.hydrateFromStorage();
    if (this.token) return this.token;
    const ok = await this.runSharedRefresh();
    return ok ? this.token : null;
  }

  async handleResponse(response: Response): Promise<unknown> {
    if (!response.ok) {
      if (response.status === 401) {
        const refreshed = await this.runSharedRefresh();
        if (refreshed) {
          throw new Error("RETRY_WITH_NEW_TOKEN");
        }
        dispatchAuthLogout();
        throw createAuthSessionExpiredError(401);
      }

      let raw = "";
      try {
        raw = await response.text();
      } catch {
        /* ignore */
      }

      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      try {
        if (raw) {
          const errJson = JSON.parse(raw) as { message?: string; error?: string };
          errorMessage = errJson.message || errJson.error || raw || errorMessage;
        }
      } catch {
        if (raw) errorMessage = raw;
      }

      const error = new Error(errorMessage) as Error & { status?: number; statusText?: string };
      error.status = response.status;
      error.statusText = response.statusText;
      throw error;
    }

    const contentType = response.headers.get("content-type") || "";
    if (contentType.includes("application/json")) {
      return response.json();
    }
    const raw = await response.text();
    try {
      return JSON.parse(raw);
    } catch {
      return raw;
    }
  }

  async request(
    method: string,
    endpoint: string,
    options: {
      data?: unknown;
      headers?: Record<string, string>;
      fakeDelay?: number;
      retryCount?: number;
      maxRetries?: number;
    } & RequestInit = {},
  ): Promise<unknown> {
    const {
      data = null,
      headers = {},
      fakeDelay = this.fakeDelay,
      retryCount = 0,
      maxRetries = 1,
      ...fetchOptions
    } = options;

    const url = this.buildURL(endpoint);
    const requestHeaders = { ...this.getHeaders(), ...headers };
    const requestOptions: RequestInit = {
      method: method.toUpperCase(),
      headers: requestHeaders,
      ...fetchOptions,
    };

    if (data != null && ["POST", "PUT", "PATCH"].includes(method.toUpperCase())) {
      requestOptions.body = JSON.stringify(data);
    }

    try {
      await this.simulateDelay(fakeDelay);
      const response = await fetch(url, requestOptions);
      return await this.handleResponse(response);
    } catch (error: unknown) {
      const err = error as Error & { status?: number };
      if (err.message === "RETRY_WITH_NEW_TOKEN" && retryCount < maxRetries) {
        return this.request(method, endpoint, { ...options, retryCount: retryCount + 1 });
      }
      if (err.status != null) throw err;
      const networkError = new Error(`Network error: ${err.message}`) as Error & {
        originalError?: unknown;
      };
      networkError.originalError = error;
      throw networkError;
    }
  }

  async get(endpoint: string, options?: Parameters<ApiClient["request"]>[2]) {
    return this.request("GET", endpoint, options ?? {});
  }

  async post(endpoint: string, data: unknown = null, options?: Omit<Parameters<ApiClient["request"]>[2], "data">) {
    return this.request("POST", endpoint, { ...options, data });
  }

  async put(endpoint: string, data: unknown = null, options?: Omit<Parameters<ApiClient["request"]>[2], "data">) {
    return this.request("PUT", endpoint, { ...options, data });
  }

  async patch(endpoint: string, data: unknown = null, options?: Omit<Parameters<ApiClient["request"]>[2], "data">) {
    return this.request("PATCH", endpoint, { ...options, data });
  }

  async delete(endpoint: string, options?: Parameters<ApiClient["request"]>[2]) {
    return this.request("DELETE", endpoint, options ?? {});
  }

  setToken(token: string | null): void {
    this.token = token;
  }

  clearToken(): void {
    this.token = null;
  }

  async clearAllTokens(): Promise<void> {
    this.clearToken();
    await tokenStorage.clear();
  }

  setBaseURL(baseURL: string): void {
    this.baseURL = baseURL;
  }

  setFakeDelay(delay: number): void {
    this.fakeDelay = delay;
  }

  // --- API methods (same paths as legacy ApiClient.js) ---

  async getPublicConfig() {
    return this.get("/config/public");
  }

  async getLanguages() {
    return this.get("v1/languages");
  }

  async getTranslations(lang: string, options: { ifNoneMatch?: string } = {}) {
    const { ifNoneMatch } = options;
    const endpoint = `v1/translations/${lang}`;
    const url = this.buildURL(endpoint);
    const headers: Record<string, string> = { ...this.defaultHeaders, ...this.getAuthHeader() };
    if (ifNoneMatch) {
      headers["If-None-Match"] = ifNoneMatch.includes('"') ? ifNoneMatch : `"${ifNoneMatch}"`;
    }
    await this.simulateDelay(this.fakeDelay);
    const response = await fetch(url, { method: "GET", headers });
    const etag = response.headers.get("ETag");
    if (response.status === 304) {
      return { notModified: true as const, etag };
    }
    if (!response.ok) {
      if (response.status === 401) {
        const ok = await this.runSharedRefresh();
        if (ok) {
          return this.getTranslations(lang, options);
        }
        dispatchAuthLogout();
        throw createAuthSessionExpiredError(401);
      }
      let message = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const raw = await response.text();
        if (raw) {
          try {
            const j = JSON.parse(raw) as { message?: string; error?: string };
            message = j.message || j.error || message;
          } catch {
            message = raw;
          }
        }
      } catch {
        /* ignore */
      }
      const error = new Error(message) as Error & { status?: number };
      error.status = response.status;
      throw error;
    }
    const data = await response.json();
    return { notModified: false as const, data, etag };
  }

  async getTokenPackages() {
    return this.get("/billing/token-packages");
  }

  async getSubscriptionPlans(platform?: string) {
    const params = new URLSearchParams();
    if (platform) params.append("platform", platform);
    const q = params.toString();
    return this.get(`/billing/subscription-plans${q ? `?${q}` : ""}`);
  }

  async createCheckoutSession(
    packageId: string,
    platform?: string,
    successUrl?: string,
    cancelUrl?: string,
  ) {
    const params = new URLSearchParams({ packageId });
    if (platform) params.append("platform", platform);
    if (successUrl) params.append("successUrl", successUrl);
    if (cancelUrl) params.append("cancelUrl", cancelUrl);
    return this.post(`/billing/create-checkout?${params.toString()}`);
  }

  async createSubscriptionCheckout(
    plan: string,
    platform?: string,
    successUrl?: string,
    cancelUrl?: string,
  ) {
    const params = new URLSearchParams({ plan });
    if (platform) params.append("platform", platform);
    if (successUrl) params.append("successUrl", successUrl);
    if (cancelUrl) params.append("cancelUrl", cancelUrl);
    return this.post(`/billing/create-subscription-checkout?${params.toString()}`);
  }

  async createCustomerPortal(platform?: string, returnUrl?: string) {
    const params = new URLSearchParams();
    if (platform) params.append("platform", platform);
    if (returnUrl) params.append("returnUrl", returnUrl);
    const q = params.toString();
    return this.post(`/billing/customer-portal${q ? `?${q}` : ""}`);
  }

  async verifyCheckoutSession(sessionId: string) {
    return this.get(`/billing/verify-checkout-session?session_id=${sessionId}`);
  }

  async generateImage(params: { userPrompt: string; boyfriendId: string; userPhotoBase64?: string } = {}) {
    const { userPrompt, boyfriendId, userPhotoBase64 } = params;
    return this.post(`/images/generate`, { userPrompt, boyfriendId, userPhotoBase64 }) as Promise<{
      success?: boolean;
      imageUrl?: string;
      imageId?: string;
    }>;
  }

  async getUserGeneratedImagesByBoyfriend(boyfriendId: string) {
    return this.get(`/images/user-generated/boyfriend/${boyfriendId}`);
  }

  async reportGeneratedImage(imageId: string, reason: string) {
    return this.post(`/images/user-generated/${imageId}/report`, { reason });
  }

  async unlockImage(unlockKey: string) {
    return this.post("/images/unlock", { unlockKey });
  }

  async getSpicyPhotos(boyfriendId: string) {
    return this.get(`/boyfriends/${boyfriendId}/spicy-photos`);
  }

  async addTokens(tokens: number) {
    return this.post("/users/addTokens", tokens);
  }

  async getMyTokenTransactions(limit = 3) {
    return this.get(`/users/me/token-transactions?limit=${encodeURIComponent(String(limit))}`);
  }

  async updatePassword(oldPassword: string, newPassword: string) {
    return this.put("/users/me/password", { oldPassword, newPassword });
  }

  async updateEmail(password: string, newEmail: string) {
    return this.put("/users/me/email", { password, newEmail });
  }

  async resendEmailVerification() {
    return this.post("/users/me/resend-email-verification", {});
  }

  async setCredentials(email: string, password: string) {
    return this.put("/users/me/credentials", { email, password });
  }

  async getUserProfile() {
    return this.get("/users/me");
  }

  async updateUserProfile(profileData: Record<string, unknown>) {
    return this.put("/users/me", profileData);
  }

  async updatePreferredLanguage(preferredLanguage: string) {
    return this.put("/users/me/preferred-language", { preferredLanguage });
  }

  async updateNotifications(notificationsAllowed: boolean) {
    return this.put("/users/me/notifications", { notificationsAllowed });
  }

  async deleteAccount() {
    return this.delete("/users/me");
  }

  async reportChatMessage(boyfriendId: string, messageId: string, reason: string) {
    return this.post(`/chats/${boyfriendId}/messages/${messageId}/report`, { reason });
  }

  async generateBoyfriendVoice(params: { boyfriendId: string; text: string; languageCode: string }) {
    const url = this.buildURL("/voice/tts");
    const response = await fetch(url, {
      method: "POST",
      headers: { ...this.getHeaders() },
      body: JSON.stringify(params),
    });
    if (!response.ok) {
      await this.handleResponse(response);
    }
    return response.blob();
  }

  /** Whisper STT; audio is not stored server-side. */
  async transcribeVoiceMessage(params: { file: File; language?: string }) {
    const { file, language } = params;
    const url = this.buildURL("/voice/transcribe");
    const formData = new FormData();
    formData.append("file", file);
    if (language && language.trim()) {
      formData.append("language", language.trim());
    }
    const headers = this.getAuthHeader();
    const response = await fetch(url, {
      method: "POST",
      headers,
      body: formData,
    });
    return this.handleResponse(response) as Promise<{ success?: boolean; text?: string }>;
  }

  async getDebugPromptParams(messageExchangeId: string) {
    return this.get(`/debug/prompt-params/by-message-exchange/${messageExchangeId}`);
  }
}

const apiClient = new ApiClient();

export { apiClient };
export default apiClient;
